import { Component, ElementRef, Input, OnDestroy, AfterViewInit, SimpleChanges, OnChanges, NgZone } from '@angular/core';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min';
import { LocalService } from './local.service';

export type NcCameraPlacementType = 'default' | 'left' | 'right' | 'top' | 'bottom' | 'back' | 'front';

@Component({
  selector: 'nc-gltf-viewer',
  templateUrl: './gltf-viewer.component.html',
  styleUrls: ['./gltf-viewer.component.less']
})
export class GltfViewerComponent implements AfterViewInit, OnDestroy, OnChanges {
  /** 模型地址 */
  @Input() ncUrl: string = '';
  /** 摄像机初始方位 */
  @Input() ncCameraPlacement: NcCameraPlacementType = 'default';
  /** 是否启用背景色 */
  @Input() set ncBg(value: boolean | string) {
    this.state.background = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  /** 背景色值 */
  @Input() set ncBgColor(value: string) {
    this.state.backgroundColor = value;
  }
  /** 操作提示 */
  private _tips: boolean = false;
  @Input() set ncTips(value: boolean | string) {
    this._tips = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncTips(): boolean {
    return this._tips;
  }
  /** 控制面板 */
  private _control: boolean = false;
  @Input() set ncControl(value: boolean | string) {
    this._control = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncControl(): boolean {
    return this._control;
  }
  /** 背景是否透明 */
  private _alpha: boolean = false;
  @Input() set ncAlpha(value: boolean | string) {
    this._alpha = value !== null && value !== undefined && value !== false && value !== 'false';
  }
  get ncAlpha(): boolean {
    return this._alpha;
  }

  showLoading: boolean = false;
  domId = '#gltfViewer';
  camera: any;
  scene: any;
  loader: any;
  renderer: any;
  domWidth: any;
  domHeight: any;
  gltf: any;
  size: number = 0;
  center: any;
  controls: any;
  //动画
  mixer: THREE.AnimationMixer | null = null;
  clock: THREE.Clock = new THREE.Clock();

  gui: any;
  state: any = {
    toneMapping: THREE.LinearToneMapping,
    toneMappingExposure: 0,
    background: false,
    backgroundColor: '#191919',
    axesHelper: false,
    wireframe: false,
    autoRotate: false,
    animationIndex: -1,
    ambientLight: true,
    ambientLightColor: '#FFFFFF',
    ambientLightIntensity: 2,
    directionalLight: false,
    directionalLightColor: '#FFFFFF',
    directionalLightIntensity: 1,
    directionalLightPosition: {
      x: 0,
      y: 0,
      z: 0
    },
    directionalLightHelper: false,
    pointLight: false,
    pointLightColor: '#FFFFFF',
    pointLightIntensity: 1,
    pointLightDistance: 0,
    pointLightDecay: 0.1,
    pointLightPosition: {
      x: 0,
      y: 0,
      z: 0
    },
    pointLightHelper: false
  }

  constructor(private eleRef: ElementRef, private ls: LocalService, private ngZone: NgZone) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ncUrl'] && !changes['ncUrl'].firstChange) {
      this.updateModel();
    }
    if (changes['ncCameraPlacement'] && !changes['ncCameraPlacement'].firstChange) {
      this.setCameraPlacement();
    }
    if (changes['ncBg'] && !changes['ncBg'].firstChange
      || changes['ncBgColor'] && !changes['ncBgColor'].firstChange) {
      this.updateBackground();
    }
  }

  ngAfterViewInit(): void {
    this.onWindowResize = this.onWindowResize.bind(this);
    this.animate = this.animate.bind(this);
    if (this.ls.isBrowser) {
      this.ngZone.runOutsideAngular(() => {
        window.addEventListener('resize', this.onWindowResize);
      });
    }
    setTimeout(() => {
      this.ngZone.runOutsideAngular(() => {
        this.init();
      });
    })
  }

  ngOnDestroy(): void {
    if (this.ls.isBrowser) {
      window.removeEventListener('resize', this.onWindowResize);
    }
    this.webglLoseContext();
  }

  init() {
    let gltfArea: any = this.eleRef.nativeElement.querySelector(this.domId);
    this.domWidth = gltfArea.offsetWidth;
    this.domHeight = gltfArea.offsetHeight;

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: this.ncAlpha });
    this.renderer.setPixelRatio(gltfArea.devicePixelRatio);
    this.renderer.setSize(this.domWidth, this.domHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    gltfArea.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    const environment = new RoomEnvironment();
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = pmremGenerator.fromScene(environment).texture;

    const ktx2Loader = new KTX2Loader().setTranscoderPath('js/libs/basis/').detectSupport(this.renderer);
    this.loader = new GLTFLoader(new THREE.LoadingManager());
    this.loader.setKTX2Loader(ktx2Loader);
    this.loader.setMeshoptDecoder(MeshoptDecoder);
    this.updateModel();
  }

  render() {
    this.renderer.render(this.scene, this.camera);
  }

  animate() {
    requestAnimationFrame(this.animate);
    // required if controls.enableDamping or controls.autoRotate are set to true
    if (this.controls) {
      this.controls.update();
    }
    if (this.scene && this.camera) {
      this.renderer.render(this.scene, this.camera);
    }

    const delta = this.clock.getDelta();
    // 更新动画混合器
    if (this.mixer) {
      this.mixer.update(delta);
    }
  }

  onWindowResize() {
    setTimeout(() => {
      let gltfArea: any = this.eleRef.nativeElement.querySelector(this.domId);
      const width = gltfArea.offsetWidth;
      const height = gltfArea.offsetHeight;
      this.renderer.setPixelRatio(gltfArea.devicePixelRatio);
      this.renderer.setSize(width, height);
      this.render();
    })
  }

  //更新模型
  updateModel() {
    if (!this.ncUrl) {
      return;
    }

    this.showLoading = true;

    if (this.gltf) {
      this.scene.remove(this.gltf.scene);
      this.gltf = null;
    }

    this.loader.load(this.ncUrl, (gltf: any) => {
      this.showLoading = false;

      this.gltf = gltf;

      const box = new THREE.Box3().setFromObject(this.gltf.scene);
      this.size = box.getSize(new THREE.Vector3()).length();
      this.center = box.getCenter(new THREE.Vector3());

      //将坐标系原点设置在模型中心
      this.gltf.scene.position.x -= this.center.x;
      this.gltf.scene.position.y -= this.center.y;
      this.gltf.scene.position.z -= this.center.z;

      //PerspectiveCamera( fov : Number, aspect : Number, near : Number, far : Number )
      // fov — 摄像机视锥体垂直视野角度，眼睛张开的角度，0闭眼
      // aspect — 摄像机视锥体长宽比
      // near — 摄像机视锥体近端面，能看到的最近垂直距离
      // far — 摄像机视锥体远端面，能看到的最远垂直距离
      this.camera = new THREE.PerspectiveCamera(50, this.domWidth / this.domHeight, this.size / 100, this.size * 100);
      this.camera.updateProjectionMatrix();
      //position-摄像机位置
      //lookAt-摄像机望向位置  
      this.setCameraPlacement();

      //controls
      this.controls = new OrbitControls(this.camera, this.renderer.domElement);

      //根据模型size初始化state部分参数
      this.state.directionalLightPosition.x = 0;
      this.state.directionalLightPosition.y = this.size / 2;
      this.state.directionalLightPosition.z = 0;
      this.state.pointLightPosition.x = this.size / 2;
      this.state.pointLightPosition.y = 0;
      this.state.pointLightPosition.z = 0;
      this.state.pointLightDistance = this.size;

      //有动画默认播放第一个动画
      if (this.gltf.animations && this.gltf.animations.length) {
        this.state.animationIndex = 0;
      } else {
        this.state.animationIndex = -1;
      }

      //添加GUI控制面板
      if (this.ncControl) {
        this.addGUI();
      } else {
        if (this.gui) {
          this.gui.destroy();
          this.gui = null;
        }
      }

      //根据state参数初始化相关设置
      this.updateToneMapping();
      this.updateToneMappingExposure();
      this.updateBackground();
      this.toggleAxesHelper();
      this.toggleWireframe();
      this.toggleAutoRotate();
      this.updateAnimation();
      this.toggleAmbientLight();
      // this.updateAmbientLight();
      this.toggleDirectionalLight();
      // this.updateDirectionalLight();
      this.toggleDirectionalLightHelper();
      this.togglePointLight();
      // this.updatePointLight();
      this.togglePointLightHelper();

      this.scene.add(this.gltf.scene);
      this.animate();
    });
  }

  //设置相机角度和望向位置
  setCameraPlacement() {
    switch (this.ncCameraPlacement) {
      case 'left'://x-红
        this.camera.position.set(-this.size, 0, 0);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'right'://x-红
        this.camera.position.set(this.size, 0, 0);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'top'://y-绿
        this.camera.position.set(0, this.size, 0);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'bottom'://y-绿
        this.camera.position.set(0, -this.size, 0);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'front'://z-蓝
        this.camera.position.set(0, 0, this.size);
        this.camera.lookAt(0, 0, 0);
        break;
      case 'back'://z-蓝
        this.camera.position.set(0, 0, -this.size);
        this.camera.lookAt(0, 0, 0);
        break;
      default:
        this.camera.position.copy(this.center);
        // this.camera.position.set(0, 0, 0);
        this.camera.position.x += this.size / 2;
        this.camera.position.y += this.size / 5;
        this.camera.position.z += this.size / 2;
        this.camera.lookAt(this.center);
        break;
    }
  }

  //add GUI
  addGUI() {
    //折叠参数
    let closeState: any = {
      gui: false,
      displayFolder: false,
      ambientLightFolder: true,
      directionalLightFolder: true,
      directionalLightHelperFolder: false,
      pointLightFolder: true,
      pointlLightHelperFolder: false
    }
    if (this.gui) {
      //记录上个gui的面板折叠值
      closeState = {
        gui: this.gui._closed,
        displayFolder: this.gui.children[0]._closed,
        ambientLightFolder: this.gui.children[1]._closed,
        directionalLightFolder: this.gui.children[2]._closed,
        directionalLightHelperFolder: this.gui.children[2].children[3]._closed,
        pointLightFolder: this.gui.children[3]._closed,
        pointlLightHelperFolder: this.gui.children[3].children[5]._closed
      }
      this.gui.destroy();
      this.gui = null;
    }
    this.gui = new GUI({ title: '控制面板', container: this.eleRef.nativeElement.querySelector('#control') });
    if (closeState.gui) {
      this.gui.close();
    }
    const displayFolder = this.gui.addFolder('显示设置');
    if (closeState.displayFolder) {
      displayFolder.close();
    }
    displayFolder.add(this.state, 'toneMapping',
      {
        'ACESFilmic': THREE.ACESFilmicToneMapping,
        'Linear': THREE.LinearToneMapping,
        'Reinhard': THREE.ReinhardToneMapping,
        'Cineon': THREE.CineonToneMapping,
        'AgX': THREE.AgXToneMapping,
        'Neutral': THREE.NeutralToneMapping
      }
    ).name('色调').onChange(() => this.updateToneMapping());
    displayFolder.add(this.state, 'toneMappingExposure', -10, 5, 0.01).name('曝光').onChange(() => this.updateToneMappingExposure());
    displayFolder.add(this.state, 'background').name('启用背景').listen().onChange(() => this.updateBackground());
    displayFolder.addColor(this.state, 'backgroundColor').name('背景色').listen().onChange(() => this.updateBackground());
    displayFolder.add(this.state, 'axesHelper').name('坐标系').onChange(() => this.toggleAxesHelper());
    displayFolder.add(this.state, 'wireframe').name('线框模式').onChange(() => this.toggleWireframe());
    displayFolder.add(this.state, 'autoRotate').name('自动旋转').onChange(() => this.toggleAutoRotate());
    let animationList: any = { '无': -1 };
    if (this.gltf.animations && this.gltf.animations.length) {
      this.gltf.animations.map((animation: any, index: number) => {
        animationList[animation.name ? animation.name : '动画' + (index + 1)] = index;
      });
    }
    displayFolder.add(this.state, 'animationIndex', animationList).listen().name('动画').onChange(() => this.updateAnimation());

    const ambientLightFolder = this.gui.addFolder('环境光');
    if (closeState.ambientLightFolder) {
      ambientLightFolder.close();
    }
    ambientLightFolder.add(this.state, 'ambientLight').name('启用').onChange(() => this.toggleAmbientLight());
    ambientLightFolder.addColor(this.state, 'ambientLightColor').name('颜色').onChange(() => this.updateAmbientLight());
    ambientLightFolder.add(this.state, 'ambientLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updateAmbientLight());

    const directionalLightFolder = this.gui.addFolder('定向光');
    if (closeState.directionalLightFolder) {
      directionalLightFolder.close();
    }
    directionalLightFolder.add(this.state, 'directionalLight').name('启用').onChange(() => this.toggleDirectionalLight());
    directionalLightFolder.addColor(this.state, 'directionalLightColor').name('颜色').onChange(() => this.updateDirectionalLight());
    directionalLightFolder.add(this.state, 'directionalLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updateDirectionalLight());
    const directionalLightHelperFolder = directionalLightFolder.addFolder('定向光位置');
    if (closeState.directionalLightHelperFolder) {
      directionalLightHelperFolder.close();
    }
    directionalLightHelperFolder.add(this.state, 'directionalLightHelper').listen().name('位置模拟').onChange(() => this.toggleDirectionalLightHelper());
    directionalLightHelperFolder.add(this.state.directionalLightPosition, 'x', -this.size, this.size).name('x（坐标系-红）').decimals(2).onChange(() => this.updateDirectionalLight());
    directionalLightHelperFolder.add(this.state.directionalLightPosition, 'y', -this.size, this.size).name('y（坐标系-绿）').decimals(2).onChange(() => this.updateDirectionalLight());
    directionalLightHelperFolder.add(this.state.directionalLightPosition, 'z', -this.size, this.size).name('z（坐标系-蓝）').decimals(2).onChange(() => this.updateDirectionalLight());

    const pointLightFolder = this.gui.addFolder('点光源');
    if (closeState.pointLightFolder) {
      pointLightFolder.close();
    }
    pointLightFolder.add(this.state, 'pointLight').name('启用').onChange(() => this.togglePointLight());
    pointLightFolder.addColor(this.state, 'pointLightColor').name('颜色').onChange(() => this.updatePointLight());
    pointLightFolder.add(this.state, 'pointLightIntensity', 0, 10, 0.1).name('强度').onChange(() => this.updatePointLight());
    pointLightFolder.add(this.state, 'pointLightDistance', 0, this.size).name('范围').decimals(2).onChange(() => this.updatePointLight());
    pointLightFolder.add(this.state, 'pointLightDecay', 0, 2, 0.1).name('衰减').onChange(() => this.updatePointLight());
    const pointlLightHelperFolder = pointLightFolder.addFolder('点光源位置');
    if (closeState.pointlLightHelperFolder) {
      pointlLightHelperFolder.close();
    }
    pointlLightHelperFolder.add(this.state, 'pointLightHelper').listen().name('位置模拟').onChange(() => this.togglePointLightHelper());
    pointlLightHelperFolder.add(this.state.pointLightPosition, 'x', -this.size, this.size).name('x（坐标系-红）').decimals(2).onChange(() => this.updatePointLight());
    pointlLightHelperFolder.add(this.state.pointLightPosition, 'y', -this.size, this.size).name('y（坐标系-绿）').decimals(2).onChange(() => this.updatePointLight());
    pointlLightHelperFolder.add(this.state.pointLightPosition, 'z', -this.size, this.size).name('z（坐标系-蓝）').decimals(2).onChange(() => this.updatePointLight());
  }

  //色调
  updateToneMapping() {
    this.renderer.toneMapping = this.state.toneMapping;
  }

  //曝光
  updateToneMappingExposure() {
    this.renderer.toneMappingExposure = Math.pow(2, this.state.toneMappingExposure);
  }

  //背景
  updateBackground() {
    if (this.state.background) {
      this.scene.background = new THREE.Color(this.state.backgroundColor);
    } else {
      this.scene.background = null;
    }
  }

  //开关辅助观察坐标系
  axesHelper: any;
  toggleAxesHelper() {
    //x-红，y-绿，z-蓝
    if (this.state.axesHelper) {
      if (this.axesHelper) {
        this.scene.remove(this.axesHelper);
        this.axesHelper = null;
      }
      this.axesHelper = new THREE.AxesHelper(this.size);
      this.scene.add(this.axesHelper);
    } else {
      if (this.axesHelper) {
        this.scene.remove(this.axesHelper);
        this.axesHelper = null;
      }
    }
  }

  //线框模式
  toggleWireframe() {
    this.traverseMaterials(this.gltf.scene, (material: any) => {
      material.wireframe = this.state.wireframe;

      if (material instanceof THREE.PointsMaterial) {
        material.size = 1;
      }
    });
  }

  //遍历材质
  traverseMaterials(object: any, callback: any) {
    object.traverse((node: any) => {
      if (!node.geometry) return;
      const materials = Array.isArray(node.material) ? node.material : [node.material];
      materials.forEach(callback);
    });
  }

  //自动旋转
  toggleAutoRotate() {
    this.controls.autoRotate = this.state.autoRotate;
  }

  //开关动画
  updateAnimation() {
    //先停止所有动画，否者关闭动画后会存在动作残留
    if (this.mixer) {
      this.mixer.stopAllAction();
      this.mixer = null;
    }

    if (this.gltf.animations && this.gltf.animations.length && this.state.animationIndex != -1) {
      this.mixer = new THREE.AnimationMixer(this.gltf.scene);
      let animation: THREE.AnimationClip = this.gltf.animations[this.state.animationIndex];
      if (this.mixer) {
        this.mixer.clipAction(animation).play();
      }
    }
  }

  //开关环境光
  ambientLight: any;
  toggleAmbientLight() {
    if (this.state.ambientLight) {
      if (this.ambientLight) {
        this.scene.remove(this.ambientLight);
        this.ambientLight = null;
      }
      this.ambientLight = new THREE.AmbientLight(this.state.ambientLightColor, this.state.ambientLightIntensity);
      this.scene.add(this.ambientLight);
    } else {
      if (this.ambientLight) {
        this.scene.remove(this.ambientLight);
        this.ambientLight = null;
      }
    }
  }
  //更新环境光
  updateAmbientLight() {
    if (this.state.ambientLight) {
      this.ambientLight.color.set(this.state.ambientLightColor);
      this.ambientLight.intensity = this.state.ambientLightIntensity;
    }
  }

  //开关定向光
  directionalLight: any;
  toggleDirectionalLight() {
    if (this.state.directionalLight) {
      if (this.directionalLight) {
        this.scene.remove(this.directionalLight);
        this.directionalLight = null;
      }
      this.directionalLight = new THREE.DirectionalLight(this.state.directionalLightColor, this.state.directionalLightIntensity);
      this.directionalLight.position.set(
        this.state.directionalLightPosition.x,
        this.state.directionalLightPosition.y,
        this.state.directionalLightPosition.z
      );
      this.scene.add(this.directionalLight);
    } else {
      if (this.directionalLight) {
        this.scene.remove(this.directionalLight);
        this.directionalLight = null;
      }
      //关闭位置模拟
      this.state.directionalLightHelper = false;
      this.toggleDirectionalLightHelper();
    }
  }
  //更新定向光
  updateDirectionalLight() {
    if (this.state.directionalLight) {
      this.directionalLight.color.set(this.state.directionalLightColor);
      this.directionalLight.intensity = this.state.directionalLightIntensity;
      this.directionalLight.position.set(
        this.state.directionalLightPosition.x,
        this.state.directionalLightPosition.y,
        this.state.directionalLightPosition.z
      );
      if (this.directionalLightHelper) {
        this.directionalLightHelper.update()
      }
    }
  }
  //开关定向光位置模拟
  directionalLightHelper: any;
  toggleDirectionalLightHelper() {
    if (this.state.directionalLightHelper) {
      if (this.directionalLight) {//开启定向光才能开启位置模拟
        if (this.directionalLightHelper) {
          this.scene.remove(this.directionalLightHelper);
          this.directionalLightHelper = null;
        }
        this.directionalLightHelper = new THREE.DirectionalLightHelper(this.directionalLight, this.size / 20, 0xff0000);
        this.scene.add(this.directionalLightHelper);
      } else {
        this.state.directionalLightHelper = false;
      }
    } else {
      if (this.directionalLightHelper) {
        this.scene.remove(this.directionalLightHelper);
        this.directionalLightHelper = null;
      }
    }
  }

  //开关点光源
  pointLight: any;
  togglePointLight() {
    if (this.state.pointLight) {
      if (this.pointLight) {
        this.scene.remove(this.pointLight);
        this.pointLight = null;
      }
      //PointLight( color : Integer, intensity : Float, distance : Number, decay : Float )
      this.pointLight = new THREE.PointLight(this.state.pointLightColor, this.state.pointLightIntensity, this.state.pointLightDistance, this.state.pointLightDecay);
      this.pointLight.position.set(
        this.state.pointLightPosition.x,
        this.state.pointLightPosition.y,
        this.state.pointLightPosition.z
      );
      this.scene.add(this.pointLight);
    } else {
      if (this.pointLight) {
        this.scene.remove(this.pointLight);
        this.pointLight = null;
      }
      //关闭位置模拟
      this.state.pointLightHelper = false;
      this.togglePointLightHelper();
    }
  }

  //更新点光源
  updatePointLight() {
    if (this.state.pointLight) {
      this.pointLight.color.set(this.state.pointLightColor);
      this.pointLight.intensity = this.state.pointLightIntensity;
      this.pointLight.decay = this.state.pointLightDecay;
      this.pointLight.distance = this.state.pointLightDistance;
      this.pointLight.position.set(
        this.state.pointLightPosition.x,
        this.state.pointLightPosition.y,
        this.state.pointLightPosition.z
      );
      if (this.pointLightHelper) {
        this.pointLightHelper.update()
      }
    }
  }
  //开关点光源位置模拟
  pointLightHelper: any;
  togglePointLightHelper() {
    if (this.state.pointLightHelper) {
      if (this.pointLight) {//开启点光源才能开启位置模拟
        if (this.pointLightHelper) {
          this.scene.remove(this.pointLightHelper);
          this.pointLightHelper = null;
        }
        this.pointLightHelper = new THREE.PointLightHelper(this.pointLight, this.size / 20, 0x2bff00);
        this.scene.add(this.pointLightHelper);
      } else {
        this.state.pointLightHelper = false;
      }
    } else {
      if (this.pointLightHelper) {
        this.scene.remove(this.pointLightHelper);
        this.pointLightHelper = null;
      }
    }
  }

  //webgl销毁
  webglLoseContext() {
    let gltfCanvasDom: any = this.eleRef.nativeElement.querySelector(`${this.domId} canvas`);
    if (gltfCanvasDom) {
      //手动销毁WebGL context对象（最多16个，超出的话最先加载的会被移除）
      let gl = gltfCanvasDom.getContext('webgl2');
      gl.getExtension('WEBGL_lose_context').loseContext();
      //dom移除
      gltfCanvasDom.remove();
    }
  }

}
